/**
 * Credit metering on the frontend (plan 2026-11-01 credit metering, Step 10).
 *
 * Pure helpers shared by every pricing surface. Prices are never typed here: they come from
 * `@digital-twins/shared-types` (`roomMessageCredits`, `assistantLevelRate`, `typicalCredits`,
 * `CREDIT_PRICING_POLICY_VERSION`) or from the server. Only an account whose org is `on` sees
 * the new prices; `off` and `shadow` see exactly today's.
 */
import { useCreditInfo } from '@/lib/hooks'

import type { CreditInfo } from '@/lib/api'

export type MeteringMode = NonNullable<CreditInfo['meteringMode']>

/** Enrolled means `on`. `shadow` prices and records but shows nothing new. */
export function isMeteringEnrolled(info: Pick<CreditInfo, 'meteringMode'> | null | undefined): boolean {
  return info?.meteringMode === 'on'
}

export function useMeteringEnrolled(): boolean {
  const { data } = useCreditInfo()
  return isMeteringEnrolled(data)
}

// ── Assistant typical cost ────────────────────────────────────────────────────

/**
 * The picker's typical credits per message: one decimal, half up, float-safe (0.35 gives 0.4,
 * 0.175 gives 0.2). Null when the server sent nothing usable (no line is shown).
 */
export function assistantTypicalCost(typicalCreditsPerMessage: number | null | undefined): number | null {
  if (typeof typicalCreditsPerMessage !== 'number' || !Number.isFinite(typicalCreditsPerMessage) || typicalCreditsPerMessage <= 0) {
    return null
  }
  return Math.round(typicalCreditsPerMessage * 10 + 1e-9) / 10
}

// ── Credits from millicredits (usage log, reply cost) ─────────────────────────

/**
 * Credits shown for an exact amount in millicredits. Zero is "0"; a positive amount below
 * one hundredth is `<0.01`, never rounded down to zero. Two decimals at most.
 */
export function formatMillicredits(millicredits: number): string {
  if (!Number.isFinite(millicredits) || millicredits <= 0) {
    return '0'
  }
  if (millicredits < 10) {
    return '<0.01'
  }
  const credits = Math.round(millicredits / 10) / 100
  return String(credits)
}

export * from './room-pricing'
